// fetchStreamsToken.js
'use strict';

import getLiveStreamData from '../selector/getLiveStreamData.js';
import getMeData from '../selector/getMeData.js';
import getNetworkingData from '../selector/getNetworkingData.js';
import getOperationData from '../selector/getOperationData.js';

import setStreamToken from '../action/setStreamToken.js';

import fetch from '../resource/customFetch.js';
import getResourceUrl from '../resource/getResourceUrl.js';
import { getHeaders } from '../resource/fetchOptionHeader.js';
import handleFetchError from '../resource/handleFetchError.js';
import { LivestreamTokenVia } from '../resource/liveStreamConstants.js';
import trackMixpanel, { EventTypes } from '../resource/mixpanel.js';

import {
  SET_NETWORKING_FETCHING,
  SET_NETWORKING_SUCCESS,
  SET_NETWORKING_ERROR,
  MERGE_LIVE_STREAM_DATA,
} from '../ActionTypes.js';

/**
 * Fetch streams token
 * @kind action
 * @param {string} {streamId} - stream id.
 * @param {string} {cacheBuster} - cache buster.
 * @param {number} {jitterFactor} - jitter factor.
 * @param {boolean} {isAgora} - is agora.
 * @return {Promise} Action promise.
 */
const fetchStreamsToken =
  ({ streamId, cacheBuster, jitterFactor, isAgora = false } = {}) =>
  async (dispatch, getState) => {
    const sessionId = getLiveStreamData(getState(), [streamId], 'sessionId');
    const networkingSelectPath = ['fetch', 'streamToken', sessionId];
    if (isAgora) {
      networkingSelectPath.push('isAgora');
    }
    const token = getMeData(getState(), 'token');
    const isFetching = getNetworkingData(
      getState(),
      networkingSelectPath,
      'isFetching'
    );

    // for agora, we need to fetch agora token even if the livestream is free
    if ((!token && !isAgora) || isFetching || !sessionId) {
      return dispatch({ type: '' });
    }
    const fetchOptions = {
      method: 'GET',
      headers: {
        ...getHeaders(),
      },
    };

    if (token) {
      fetchOptions.headers.Authorization = `Bearer ${token}`;
    }

    const fetchUrl = getResourceUrl({
      endpoint: `/streams/${sessionId}/token`,
    });
    if (cacheBuster) {
      fetchUrl.searchParams.set('_', cacheBuster);
    }

    const activeStreamId = getOperationData(
      getState(),
      ['liveStream'],
      'activeStreamId'
    );

    trackMixpanel({
      type: EventTypes.LIVESTREAM_FETCH_TOKEN_START,
      payload: {
        streamId,
        sessionId,
        fetchUrl: fetchUrl.href,
        activeStreamId,
      },
    });

    dispatch({
      type: SET_NETWORKING_FETCHING,
      payload: { selectPath: networkingSelectPath },
    });

    try {
      if (jitterFactor) {
        await new Promise(resolve => {
          setTimeout(resolve, Math.ceil(Math.random() * jitterFactor * 1000));
        });
      }

      let response = await fetch(fetchUrl.href, fetchOptions);
      if (!response.ok) {
        response = await handleFetchError({
          response,
          dispatch,
          getState,
          fetchOptions,
          fetchUrl,
        });
      }

      const payload = (await response.json()) || {};

      if (isAgora) {
        dispatch({
          type: MERGE_LIVE_STREAM_DATA,
          payload: {
            selectPath: [streamId, 'providerInfo', 'agora'],
            data: {
              agoraToken: payload.agora_token_session_id,
              agoraTokenNbf: payload.nbf,
              agoraTokenExp: payload.exp,
            },
          },
        });
      } else {
        dispatch(
          setStreamToken({
            streamId,
            token: payload.token,
            byteplusToken: payload.byteplus_token,
            byteplusTokenNbf: payload.nbf,
            byteplusTokenExp: payload.exp,
            via: LivestreamTokenVia.TOKEN_API,
          })
        );
      }

      return dispatch({
        type: SET_NETWORKING_SUCCESS,
        payload: { selectPath: networkingSelectPath },
      });
    } catch (error) {
      trackMixpanel({
        type: EventTypes.LIVESTREAM_FETCH_TOKEN_ERROR,
        payload: {
          streamId,
          sessionId,
          errorMessage: error.message,
          errorStatus: error.status,
        },
      });
      return dispatch({
        type: SET_NETWORKING_ERROR,
        payload: { selectPath: networkingSelectPath, error },
      });
    }
  };

export default fetchStreamsToken;
